/**
 * Truncates product descriptions by sentences instead of characters
 * Only affects product cards, not full product pages
 */

class ProductDescriptionTruncation {
  constructor() {
    this.maxSentences = 1; // Maximum number of sentences to show
    this.init();
  }

  init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.truncateDescriptions());
    } else {
      this.truncateDescriptions();
    }

    // Also run after dynamic content loads (for quick-add, etc.)
    this.observeChanges();
  }

  /**
   * Decode HTML entities
   * @param {string} text - Text with HTML entities
   * @returns {string} - Decoded text
   */
  decodeHtmlEntities(text) {
    const textarea = document.createElement('textarea');
    textarea.innerHTML = text;
    return textarea.value;
  }

  /**
   * Truncate all product descriptions by sentences
   */
  truncateDescriptions() {
    // Only target product-description elements within product cards
    const productCards = document.querySelectorAll('.product-each .product-description');
    
    productCards.forEach(element => {
      const fullDescription = element.getAttribute('data-full-description');
      
      if (!fullDescription) return;
      
      // Decode HTML entities first
      const decodedText = this.decodeHtmlEntities(fullDescription);
      
      const truncated = this.truncateBySentences(decodedText, this.maxSentences);
      element.textContent = truncated;
    });
  }

  /**
   * Truncate text to a maximum number of sentences
   * @param {string} text - The full text to truncate
   * @param {number} maxSentences - Maximum number of sentences to keep
   * @returns {string} - Truncated text
   */
  truncateBySentences(text, maxSentences) {
    if (!text || text.trim().length === 0) return text;
    
    // Clean up whitespace but preserve newlines as sentence boundaries
    text = text.trim().replace(/[ \t]+/g, ' ');
    
    // Split by sentence delimiters (., !, ?, newline) but handle abbreviations
    const sentences = this.splitIntoSentences(text);
    
    if (sentences.length <= maxSentences) {
      return text;
    }
    
    // Join the first maxSentences and add ellipsis
    const truncated = sentences.slice(0, maxSentences).join(' ');
    return truncated + '...';
  }

  /**
   * Split text into sentences, handling common abbreviations
   * @param {string} text - Text to split
   * @returns {string[]} - Array of sentences
   */
  splitIntoSentences(text) {
    const abbreviations = ['Mr', 'Mrs', 'Ms', 'Dr', 'Prof', 'Sr', 'Jr', 'vs', 'etc', 'i.e', 'e.g', 'approx', 'no', 'qty', 'cm', 'mm', 'inch', 'oz', 'lb', 'kg', 'g', 'mg'];
    
    // Create regex pattern for abbreviations
    const abbrPattern = abbreviations.join('|');
    
    // First, split by newlines to handle line breaks as sentence boundaries
    let lines = text.split(/\n+/);
    
    // Then process each line to split by punctuation
    /** @type {string[]} */
    let sentences = [];
    lines.forEach(line => {
      if (!line.trim()) return;
      
      // Split by sentence boundaries - look for ., !, or ? followed by whitespace or end
      // but not after abbreviations
      const sentenceRegex = new RegExp(
        `(?<!\\b(?:${abbrPattern})\\.)[.!?]+(?:\\s|$)`,
        'g'
      );
      
      const lineSentences = line.split(sentenceRegex);
      
      // Clean up each sentence
      lineSentences.forEach(s => {
        s = s.trim();
        if (s.length > 0) {
          // Add back punctuation if it was removed and doesn't end with punctuation
          if (!/[.!?]$/.test(s)) {
            s += '.';
          }
          sentences.push(s);
        }
      });
    });
    
    return sentences;
  }

  /**
   * Observe DOM changes for dynamic content (quick-add, AJAX, etc.)
   */
  observeChanges() {
    if (!('MutationObserver' in window)) return;
    
    const observer = new MutationObserver((mutations) => {
      let needsUpdate = false;
      
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1) { // Element node
            const element = /** @type {Element} */ (node);
            if (element.classList && element.classList.contains('product-each')) {
              needsUpdate = true;
            } else if (element.querySelector && element.querySelector('.product-each')) {
              needsUpdate = true;
            }
          }
        });
      });
      
      if (needsUpdate) {
        this.truncateDescriptions();
      }
    });
    
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }
}

// Initialize on page load
new ProductDescriptionTruncation();

export default ProductDescriptionTruncation;
